import React, { useEffect, useRef, useState } from 'react';
import { Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { renderGoogleButton } from '../../../lib/googleIdentity';

type Props = {
    clientId: string;
    onCredential: (idToken: string) => void;
    text?: 'signin_with' | 'signup_with' | 'continue_with';
};

/**
 * Bouton officiel Google Identity Services.
 *
 * Google impose son propre rendu (le bouton est injecté dans un iframe) : on ne
 * peut pas le styler nous-mêmes, on se contente donc de le centrer. Si le SDK
 * ne se charge pas (réseau, bloqueur), on l'annonce sans casser l'écran :
 * l'inscription par email reste disponible juste au-dessus.
 */
const GoogleSignInButton: React.FC<Props> = ({ clientId, onCredential, text = 'continue_with' }) => {
    const { t, i18n } = useTranslation();
    const containerRef = useRef<HTMLDivElement>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || !clientId) return;

        let cleanup: (() => void) | undefined;
        let cancelled = false;

        renderGoogleButton({
            clientId,
            container,
            text,
            locale: i18n.language,
            onCredential,
            onError: () => {
                if (!cancelled) setFailed(true);
            },
        }).then((dispose) => {
            if (cancelled) dispose();
            else cleanup = dispose;
        });

        return () => {
            cancelled = true;
            cleanup?.();
        };
    }, [clientId, onCredential, text, i18n.language]);

    if (failed) {
        return (
            <Alert
                type="warning"
                showIcon
                message={t('signup.google.unavailable', "L'identification Google est momentanément indisponible.")}
                description={t('signup.google.unavailableHint', 'Utilisez votre adresse email pour continuer.')}
            />
        );
    }

    return <div ref={containerRef} style={{ display: 'flex', justifyContent: 'center' }} />;
};

export default GoogleSignInButton;
